import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { protocolsAPI, documentTypesAPI, exportAPI } from "../lib/api";
import { formatDate, getDirectionLabel, getDirectionColor } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Search,
  Download,
  FileSpreadsheet,
  FileText,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  Link as LinkIcon,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";

export function ProtocolsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [protocols, setProtocols] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [message, setMessage] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  // Sorting
  const [sortBy, setSortBy] = useState("number");
  const [sortOrder, setSortOrder] = useState("desc");

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const SortHeader = ({ field, children }) => (
    <TableHead
      className="text-muted-foreground cursor-pointer hover:text-foreground select-none"
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === field ? (
          sortOrder === "asc" ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        ) : (
          <ChevronsUpDown size={14} className="opacity-30" />
        )}
      </div>
    </TableHead>
  );
  
  // Filters
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [direction, setDirection] = useState(searchParams.get("direction") || "");
  const [docType, setDocType] = useState(searchParams.get("type") || "");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Check for saved parameter on mount
  useEffect(() => {
    if (searchParams.get("saved") === "true") {
      showMessage("Protocollo salvato con successo");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("saved");
      setSearchParams(newParams);
    }
    if (searchParams.get("updated") === "true") {
      showMessage("Protocollo modificato con successo");
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("updated");
      setSearchParams(newParams);
      loadData(); // Force refresh data after update
    }
  }, [searchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      
      if (search) params.search = search;
      if (direction) params.direction = direction;
      if (docType) params.document_type = docType;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      if (sortBy) params.sort_by = sortBy;
      if (sortOrder) params.sort_order = sortOrder;
      
      const [protocolsRes, typesRes] = await Promise.all([
        protocolsAPI.getAll(params),
        documentTypesAPI.getAll(),
      ]);
      
      setProtocols(protocolsRes.data.items);
      setTotalPages(protocolsRes.data.total_pages);
      setTotal(protocolsRes.data.total);
      setDocumentTypes(typesRes.data);
    } catch (error) {
      showMessage("Errore nel caricamento dei dati", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, direction, docType, dateFrom, dateTo, sortBy, sortOrder]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (direction) params.set("direction", direction);
    if (docType) params.set("type", docType);
    setSearchParams(params);
  }, [search, direction, docType, setSearchParams]);

  const handleExport = async (type) => {
    setExporting(true);
    try {
      const params = {};
      if (direction) params.direction = direction;
      if (docType) params.document_type = docType;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      
      const response = type === "excel" 
        ? await exportAPI.excel(params)
        : await exportAPI.pdf(params);
      
      const blob = new Blob([response.data], {
        type: type === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registro_protocollo.${type === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      showMessage(`Export ${type.toUpperCase()} completato`);
    } catch (error) {
      showMessage("Errore durante l'export", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteClick = (protocolId) => {
    setDeleteId(protocolId);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await protocolsAPI.delete(deleteId);
      showMessage("Protocollo eliminato");
      loadData();
    } catch (error) {
      showMessage("Errore durante l'eliminazione", "error");
    } finally {
      setDeleteId(null);
      setShowDeleteDialog(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setDirection("");
    setDocType("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasFilters = search || direction || docType || dateFrom || dateTo;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo protocollo? Questa azione non puo essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              data-testid="cancel-delete"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete"
            >
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Registro Protocollo
          </h1>
          <p className="text-muted-foreground mt-1">
            {total.toLocaleString("it-IT")} protocolli registrati
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="border-border text-foreground hover:bg-muted"
                disabled={exporting}
                data-testid="export-dropdown"
              >
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2" size={18} />
                )}
                Esporta
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("excel")} data-testid="export-excel">
                <FileSpreadsheet className="mr-2" size={16} />
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf">
                <FileText className="mr-2" size={16} />
                PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Link to="/protocols/new">
            <Button
              className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
              data-testid="new-protocol-btn"
            >
              <PlusCircle className="mr-2" size={18} />
              Nuovo
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Cerca per oggetto, mittente, numero..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground"
                data-testid="search-input"
              />
            </div>
            
            {/* Direction - Shadcn Select */}
            <Select
              value={direction || undefined}
              onValueChange={(val) => {
                setDirection(val === "__all__" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="bg-background border-input" data-testid="filter-direction">
                <SelectValue placeholder="Tutte le direzioni" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le direzioni</SelectItem>
                <SelectItem value="entrata">Entrata</SelectItem>
                <SelectItem value="uscita">Uscita</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Document Type - Shadcn Select */}
            <Select
              value={docType || undefined}
              onValueChange={(val) => {
                setDocType(val === "__all__" ? "" : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="bg-background border-input" data-testid="filter-type">
                <SelectValue placeholder="Tutte le tipologie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le tipologie</SelectItem>
                {documentTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Date Range */}
            <div className="flex gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="flex-1 bg-background border-input text-foreground"
                data-testid="filter-date-from"
              />
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                className="flex-1 bg-background border-input text-foreground"
                data-testid="filter-date-to"
              />
            </div>
          </div>
          
          {hasFilters && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Filtri attivi:</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="clear-filters"
              >
                <X size={16} className="mr-1" />
                Rimuovi tutti
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-card border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="protocol-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <SortHeader field="number">N. Protocollo</SortHeader>
                <SortHeader field="date">Data Reg.</SortHeader>
                <SortHeader field="document_date">Data Doc.</SortHeader>
                <TableHead className="text-muted-foreground">Direzione</TableHead>
                <TableHead className="text-muted-foreground">Tipologia</TableHead>
                <SortHeader field="contact">Mitt./Dest.</SortHeader>
                <SortHeader field="subject">Oggetto</SortHeader>
                <TableHead className="text-muted-foreground">Collegato</TableHead>
                <TableHead className="text-muted-foreground">Inserito da</TableHead>
                <TableHead className="text-muted-foreground text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-border">
                    {[...Array(10)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-6 bg-muted" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : protocols.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-12">
                    <FileText size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Nessun protocollo trovato</p>
                  </TableCell>
                </TableRow>
              ) : (
                (() => {
                  // Find the most recently created protocol
                  let latestProtocol = protocols[0];
                  for (const p of protocols) {
                    if (new Date(p.created_at) > new Date(latestProtocol.created_at)) {
                      latestProtocol = p;
                    }
                  }
                  const latestId = latestProtocol?.id;
                  
                  return protocols.map((protocol, index) => {
                  const isLatest = protocol.id === latestId;
                  
                  return (
                  <TableRow
                    key={protocol.id}
                    className="border-border hover:bg-muted/50 transition-colors"
                    data-testid={`protocol-row-${protocol.id}`}
                    style={isLatest ? { backgroundColor: '#dcfce7', boxShadow: 'inset 4px 0 0 #22c55e' } : {}}
                  >
                    <TableCell className="font-mono font-medium">
                      {isLatest && (
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2" title="Ultimo registrato"></span>
                      )}
                      <Link 
                        to={`/protocols/${protocol.id}`}
                        className="text-[#2D7BB7] hover:underline cursor-pointer"
                        data-testid={`protocol-link-${protocol.id}`}
                      >
                        {protocol.protocol_number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-foreground">
                      {formatDate(protocol.registration_date || protocol.created_at)}
                    </TableCell>
                    <TableCell className="text-foreground">
                      {protocol.document_date ? formatDate(protocol.document_date) : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getDirectionColor(protocol.direction)} border`}>
                        {getDirectionLabel(protocol.direction)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-foreground">
                      {protocol.document_type}
                    </TableCell>
                    <TableCell className="text-foreground">
                      {protocol.contact_name || protocol.sender || protocol.recipient || "-"}
                    </TableCell>
                    <TableCell className="text-foreground max-w-[250px] truncate">
                      {protocol.subject}
                    </TableCell>
                    <TableCell>
                      {protocol.related_protocol_number ? (
                        <Link
                          to={`/protocols/${protocol.related_protocol_id}`}
                          className="flex items-center gap-1 text-[#2D7BB7] hover:underline text-sm"
                        >
                          <LinkIcon size={14} />
                          {protocol.related_protocol_number}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-foreground text-sm" data-testid={`protocol-created-by-${protocol.id}`}>
                      {protocol.created_by_name || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`protocol-actions-${protocol.id}`}
                          >
                            <MoreVertical size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/protocols/${protocol.id}`} className="flex items-center">
                              <Eye className="mr-2" size={16} />
                              Visualizza
                            </Link>
                          </DropdownMenuItem>
                          {isAdmin() && (
                            <>
                              <DropdownMenuItem asChild>
                                <Link to={`/protocols/${protocol.id}/edit`} className="flex items-center">
                                  <Edit className="mr-2" size={16} />
                                  Modifica
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteClick(protocol.id)}
                                className="text-red-600 focus:text-red-600"
                                data-testid={`delete-protocol-${protocol.id}`}
                              >
                                <Trash2 className="mr-2" size={16} />
                                Elimina
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );});
                })()
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Pagina {page} di {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-border text-foreground disabled:opacity-50"
                data-testid="prev-page"
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="border-border text-foreground disabled:opacity-50"
                data-testid="next-page"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
